import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'components/ui/button';
import Table, { ColumnConfig } from 'components/table';
import DeleteLocation from './deleteLocation';
import useVirtualTerminal from 'state/virtualTerminal/hooks/useVirtualTerminal';
import { VIRTUAL_TERMINAL } from 'state/virtualTerminal/type';
import AddLocation, { ModalType } from './addLocation';

const LocationConfiguration = (): JSX.Element => {
  const { virtualTerminal, deleteLocation, addLocation, editLocation, fetchLocationsList } =
    useVirtualTerminal();
  const { locationsList, locationUpdated, error } = virtualTerminal;

  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<VIRTUAL_TERMINAL.ILocation | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Holds the success message for the location operation currently in flight.
  // The hook swallows API errors (promises always resolve), so success/failure
  // is read off redux state below rather than from the action's promise.
  const pendingActionRef = useRef<string | null>(null);

  const handleAddLocation = () => {
    setModalType(ModalType.ADD);
    setSelectedLocation(null);
  };

  const handleEditLocation = (location: VIRTUAL_TERMINAL.ILocation) => {
    setModalType(ModalType.EDIT);
    setSelectedLocation(location);
  };

  const handleDeleteLocation = (location: VIRTUAL_TERMINAL.ILocation) => {
    setSelectedLocation(location);
    setShowDeleteModal(true);
  };

  const tableColumns: ColumnConfig<VIRTUAL_TERMINAL.ILocation>[] = [
    {
      key: 'name',
      label: 'name',
    },
    {
      key: 'id',
      label: 'Actions',
      renderCell: (row) => (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="edit"
            iconClassName="text-gray-blue-60"
            data-testid="edit_location"
            aria-label="Edit location"
            onClick={() => handleEditLocation(row)}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="trash"
            iconClassName="text-gray-blue-60"
            data-testid="delete_location"
            aria-label="Delete location"
            onClick={() => handleDeleteLocation(row)}
          />
        </>
      ),
    },
  ];

  const NoDataIndication = (
    <div className="flex items-center justify-center w-186">
      No locations to show.
      <span className="ml-1 cursor-pointer" onClick={() => handleAddLocation()}>
        Add a new location
      </span>
    </div>
  );

  const submitLocation = (request: VIRTUAL_TERMINAL.IAddLocationRequest) => {
    if (modalType === ModalType.ADD) {
      pendingActionRef.current = 'Location added successfully.';
      addLocation(request);
    } else if (modalType === ModalType.EDIT) {
      pendingActionRef.current = 'Location updated successfully.';
      editLocation(request, selectedLocation?.id as string);
    }
    setModalType(null);
  };

  const onDelete = () => {
    if (selectedLocation?.id) {
      pendingActionRef.current = 'Location deleted successfully.';
      deleteLocation(selectedLocation.id);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    fetchLocationsList();
  }, []);

  // Success path: locationUpdated flips true only on a *_SUCCESS dispatch.
  useEffect(() => {
    if (locationUpdated && pendingActionRef.current) {
      toast.success(pendingActionRef.current);
      pendingActionRef.current = null;
      fetchLocationsList();
    }
  }, [locationUpdated]);

  // Failure path: a *_FAILURE dispatch populates the matching error key.
  useEffect(() => {
    const failed =
      error.addLocation || error.editLocation || error.deleteLocation;
    if (failed && pendingActionRef.current) {
      toast.error('Something went wrong! Please try again later.');
      pendingActionRef.current = null;
    }
  }, [error.addLocation, error.editLocation, error.deleteLocation]);

  return (
    <div>
      <div className="flex justify-end mt-6 w-186">
        <Button
          variant="secondary"
          iconStart="plus"
          onClick={() => handleAddLocation()}
          className="mb-2"
        >
          Add a new location
        </Button>
      </div>
      <Table
        data={locationsList}
        columns={tableColumns}
        theme={{
          hideHeaders: true,
          tableWidth: '744px',
          tableGridLayout: '1fr auto',
        }}
        noDataIndication={NoDataIndication}
      />
      {modalType && (
        <AddLocation
          modal={!!modalType}
          setModal={() => setModalType(null)}
          type={modalType}
          onSubmit={submitLocation}
          selectedLocation={selectedLocation}
        />
      )}
      {showDeleteModal && selectedLocation && (
        <DeleteLocation
          locationName={selectedLocation?.name}
          deleteLocation={onDelete}
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
        />
      )}
    </div>
  );
};

export default LocationConfiguration;
